import type { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router';
import { useBot } from '../contexts/BotContext';
import { botPath, isBotSystem, legacyBotPath, type BotSection } from '../lib/botNav';

/** `/bots/:botId/:section` (before system was in the URL; always v1) → `/bots/chatbot/:botId/:section`. */
export function LegacyBotRedirect({ section }: { section: BotSection }) {
  const { botId = '' } = useParams();
  return <Navigate replace to={legacyBotPath(botId, section)} />;
}

/**
 * Flat bot pages (`/forms`, `/memory`, `/`) → the current bot's workspace section. With no bot chosen (fresh browser —
 * there is no default bot since A1) they show a choose-a-bot landing; the header picker opens by itself there.
 */
export function ScopedRedirect({ section }: { section: BotSection }) {
  const { system, botId } = useBot();
  if (!isBotSystem(system) || !botId) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-8">
        <h1 className="text-2xl font-bold text-gray-900">Choose a bot</h1>
        <p className="mt-4 text-gray-600">Pick a bot in the top bar to open its workspace.</p>
      </div>
    );
  }
  return <Navigate replace to={botPath({ system, botId }, section)} />;
}

/**
 * Guards every /bots/:system/:botId/* page: an unknown system (a typo like `chatbot-v2`) must not
 * render as v1 — writes would land on the wrong system. Keyed by bot so a bot switch remounts
 * the page instead of reusing state loaded for the previous bot.
 */
export function BotWorkspace({ children }: { children: ReactNode }) {
  const { system, botId = '' } = useParams();
  if (!isBotSystem(system) || !botId) return <Navigate replace to="/" />;
  return <div key={`${system}:${botId}`} className="contents">{children}</div>;
}
